import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";
import { createAuditLog } from "../utils/commonServices";
import { getLoginUsername } from "./PermissionsReducer";

const initialState = {
	vehicles: [],
	loading: false,
	error: null,
	total: 0,
};

const FETCH_VEHICLES_REQUEST = "FETCH_VEHICLES_REQUEST";
const FETCH_VEHICLES_SUCCESS = "FETCH_VEHICLES_SUCCESS";
const FETCH_VEHICLES_FAILURE = "FETCH_VEHICLES_FAILURE";
const CREATE_VEHICLE_REQUEST = "CREATE_VEHICLE_REQUEST";
const CREATE_VEHICLE_SUCCESS = "CREATE_VEHICLE_SUCCESS";
const CREATE_VEHICLE_FAILURE = "CREATE_VEHICLE_FAILURE";
const UPDATE_VEHICLE_REQUEST = "UPDATE_VEHICLE_REQUEST";
const UPDATE_VEHICLE_SUCCESS = "UPDATE_VEHICLE_SUCCESS";
const UPDATE_VEHICLE_FAILURE = "UPDATE_VEHICLE_FAILURE";
const DELETE_VEHICLE_REQUEST = "DELETE_VEHICLE_REQUEST";
const DELETE_VEHICLE_SUCCESS = "DELETE_VEHICLE_SUCCESS";
const DELETE_VEHICLE_FAILURE = "DELETE_VEHICLE_FAILURE";

// Audit Log Helper - creates audit log entry
export const logAuditEntry = async ({
	action,
	recordId,
	oldValues = null,
	newValues = null,
	changedBy,
	agencyId,
}) => {
	try {
		await createAuditLog({
			tableName: "Vehicle",
			recordId,
			action,
			changedBy: changedBy || "Unknown",
			changedAt: new Date().toISOString(),
			oldValues: oldValues ? JSON.stringify(oldValues) : null,
			newValues: newValues ? JSON.stringify(newValues) : null,
			agencyId,
		});
	} catch (error) {
		console.error("Failed to create audit log:", error);
		// Don't throw - audit failure shouldn't break main operation
	}
};

// Helper to get only changed fields
export const getChangedFields = (oldData, newData) => {
	const changes = { old: {}, new: {} };
	const fieldsToTrack = [
		"unitNumber",
		"make",
		"model",
		"year",
		"vin",
		"licensePlate",
		"status",
		"division",
		"perPersonnelId",
		"fundingSourceId",
		"mileage",
		"color",
		"notes",
	];

	fieldsToTrack.forEach((field) => {
		if (oldData[field] !== newData[field]) {
			changes.old[field] = oldData[field];
			changes.new[field] = newData[field];
		}
	});

	return changes;
};

const saveVehicleServiceRequirements = async (
	vehicleId,
	serviceRequirementIds,
	userAgency,
) => {
	try {
		const service = getService(serviceNames.perVehicleServiceRequirements);

		// Get existing service requirements
		const existingRequirements = await service.find({
			query: {
				vehicleId: vehicleId,
				isDeleted: false,
				$limit: -1,
			},
		});

		const existingIds = existingRequirements.map(
			(req) => req.serviceRequirementId,
		);
		const newIds = serviceRequirementIds || [];

		const toDelete = existingRequirements.filter(
			(req) => !newIds.includes(req.serviceRequirementId),
		);

		const toCreate = newIds.filter((id) => !existingIds.includes(id));

		// Delete removed ones
		for (const req of toDelete) {
			await service.patch(req.id, {
				isDeleted: true,
			});
		}

		// Create new ones
		for (const serviceRequirementId of toCreate) {
			await service.create({
				vehicleId: vehicleId,
				serviceRequirementId,
				agencyId: userAgency,
			});
		}

		return true;
	} catch (error) {
		console.error("Error saving service requirements:", error);
		throw error;
	}
};

export const getVehicles =
	(
		page = 0,
		pageSize = 20,
		sortField = "id",
		sortDirection = "asc",
		search = "",
		filters = {},
		userAgency,
	) =>
	async (dispatch) => {
		const service = getService(serviceNames.perVehicles);
		const query = {
			$limit: pageSize,
			$skip: page * pageSize,
			$sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
			isDeleted: false,
			agencyId: userAgency,
		};

		const textFields = ["unitNumber", "make", "model", "vin"];

		if (search) {
			query.$search = search;
		}

		textFields.forEach((field) => {
			const value = filters[field];
			if (value) {
				query[field] = filters.exactMatch
					? value
					: { $like: `%${value}%` };
			}
		});

		const getQueryValue = (input) => {
			if (Array.isArray(input)) {
				const values = input.map((i) => i.value).filter(Boolean);
				return values.length > 0 ? { $in: values } : undefined;
			} else if (input?.value) {
				return input.value;
			}
			return undefined;
		};

		const multiSelectFields = [
			"make",
			"model",
			"year",
			"perPersonnelId",
			"division",
			"fundingSourceId",
			"status",
		];

		multiSelectFields.forEach((field) => {
			const value = getQueryValue(filters[field]);
			if (value !== undefined) {
				query[field] = value;
			}
		});

		if (filters.type === "in") {
			query.perPersonnelId = { $eq: null };
		} else if (filters.type === "out") {
			query.perPersonnelId = { $ne: null };
		}

		// Handle service status filter - pass to backend
		if (filters.service && filters.service.length > 0) {
			const serviceValues = filters.service
				.map((s) => s.value)
				.filter((v) => v && v !== "all");

			if (serviceValues.length > 0) {
				query.$serviceStatus = serviceValues;
			}
		}

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: FETCH_VEHICLES_REQUEST,
				success: FETCH_VEHICLES_SUCCESS,
				failure: FETCH_VEHICLES_FAILURE,
			},
			apiCall: async () => {
				const response = await service.find({ query });

				const vehiclesData = response?.data || [];

				// Fetch service requirements for each vehicle
				const serviceRequirementsService = getService(
					serviceNames.perVehicleServiceRequirements,
				);
				const vehiclesWithServiceRequirements = await Promise.all(
					vehiclesData.map(async (vehicle) => {
						try {
							const serviceReqResponse =
								await serviceRequirementsService.find({
									query: {
										vehicleId: vehicle.id,
										isDeleted: false,
									},
								});

							// Get the service requirement codes from the response
							const serviceReqIds = Array.isArray(
								serviceReqResponse?.data,
							)
								? serviceReqResponse.data.map(
										(sr) => sr.serviceRequirementId,
									)
								: Array.isArray(serviceReqResponse)
									? serviceReqResponse.map(
											(sr) => sr.serviceRequirementId,
										)
									: [];

							return {
								...vehicle,
								serviceRequirements: serviceReqIds,
								// Include computed service status if returned from backend
								serviceStatus: vehicle.serviceStatus || null,
							};
						} catch (error) {
							console.error(
								`Error fetching service requirements for vehicle ${vehicle.id}:`,
								error,
							);
							return {
								...vehicle,
								serviceRequirements: [],
								serviceStatus: null,
							};
						}
					}),
				);

				return {
					vehicles: vehiclesWithServiceRequirements || [],
					total: response.total || 0,
				};
			},
			transformResult: (res) => {
				return {
					vehicles: res?.vehicles,
					total: res?.total,
				};
			},
			errorMessagePrefix: "Failed to fetch vehicles",
		});
	};

export const createVehicle =
	(vehicleData, serviceRequirementIds = [], userAgency, userId) =>
	async (dispatch) => {
		const service = getService(serviceNames.perVehicles);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: CREATE_VEHICLE_REQUEST,
				success: CREATE_VEHICLE_SUCCESS,
				failure: CREATE_VEHICLE_FAILURE,
			},
			apiCall: async () => {
				const vehicle = await service.create(vehicleData);
				// Save service requirements after vehicle is created
				if (serviceRequirementIds.length > 0 && vehicle.id) {
					await saveVehicleServiceRequirements(
						vehicle.id,
						serviceRequirementIds,
						userAgency,
					);
				}

				// Create Audit Log Entry
				await logAuditEntry({
					action: "CREATE",
					recordId: vehicle.id,
					oldValues: null,
					newValues: {
						...vehicleData,
						serviceRequirements: serviceRequirementIds,
					},
					changedBy: getLoginUsername() || "",
					agencyId: userAgency,
				});

				return vehicle;
			},
			successMessage: "Vehicle created successfully",
			errorMessagePrefix: "Failed to create vehicle",
		});
	};

export const updateVehicle =
	(
		id,
		vehicleData,
		serviceRequirementIds = [],
		userAgency,
		oldVehicleData = null,
	) =>
	async (dispatch) => {
		const service = getService(serviceNames.perVehicles);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_VEHICLE_REQUEST,
				success: UPDATE_VEHICLE_SUCCESS,
				failure: UPDATE_VEHICLE_FAILURE,
			},
			apiCall: async () => {
				let oldData = oldVehicleData;
				const vehicle = await service.patch(id, vehicleData);
				// Update service requirements

				await saveVehicleServiceRequirements(
					id,
					serviceRequirementIds,
					userAgency,
				);

				// Get changed fields for audit
				const changes = getChangedFields(oldData, vehicleData);

				// Only log if there are actual changes
				await logAuditEntry({
					action: "UPDATE",
					recordId: id,
					oldValues: changes.old,
					newValues: changes.new,
					changedBy: getLoginUsername() || "",
					agencyId: userAgency,
				});

				return {
					...vehicle,
					serviceRequirements: serviceRequirementIds,
				};
			},
			successMessage: "Vehicle updated successfully",
			errorMessagePrefix: "Failed to update vehicle",
		});
	};

export const deleteVehicle =
	(id, userAgency, vehicleData = null) =>
	async (dispatch) => {
		const service = getService(serviceNames.perVehicles);
		const patchData = {
			isDeleted: true,
			deletedAt: new Date().toISOString(),
		};

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: DELETE_VEHICLE_REQUEST,
				success: DELETE_VEHICLE_SUCCESS,
				failure: DELETE_VEHICLE_FAILURE,
			},
			apiCall: async () => {
				// Fetch vehicle data before deletion if not provided
				let oldData = vehicleData;
				if (!oldData) {
					try {
						oldData = await service.get(id);
					} catch (e) {
						console.error(
							"Failed to fetch vehicle data for audit:",
							e,
						);
						oldData = { id };
					}
				}

				const vehicle = await service.patch(id, patchData);
				// Delete service requirements
				const serviceReqService = getService(
					serviceNames.perVehicleServiceRequirements,
				);
				const existingReqs = await serviceReqService.find({
					query: {
						vehicleId: id,
						isDeleted: false,
					},
				});
				if (Array.isArray(existingReqs?.data)) {
					for (const req of existingReqs.data) {
						await serviceReqService.remove(req.id);
					}
				}

				// Create Audit Log Entry for deletion
				await logAuditEntry({
					action: "DELETE",
					recordId: id,
					oldValues: {
						unitNumber: oldData.unitNumber,
						make: oldData.make,
						model: oldData.model,
						year: oldData.year,
						vin: oldData.vin,
						status: oldData.status,
					},
					newValues: null,
					changedBy: getLoginUsername() || "Unknown",
					agencyId: userAgency,
				});

				return vehicle;
			},
			transformResult: () => id,
			successMessage: "Vehicle deleted successfully",
			errorMessagePrefix: "Failed to delete vehicle",
		});
	};

export default function vehiclesReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_VEHICLES_REQUEST:
		case CREATE_VEHICLE_REQUEST:
		case UPDATE_VEHICLE_REQUEST:
		case DELETE_VEHICLE_REQUEST:
			return { ...state, loading: true, error: null };
		case FETCH_VEHICLES_SUCCESS:
			return {
				...state,
				loading: false,
				vehicles: action.payload.vehicles,
				total: action.payload.total,
			};
		case CREATE_VEHICLE_SUCCESS:
			return {
				...state,
				loading: false,
				vehicles: [...state.vehicles, action.payload],
				total: state.total + 1,
			};
		case UPDATE_VEHICLE_SUCCESS:
			return {
				...state,
				loading: false,
				vehicles: state.vehicles.map((v) =>
					v.id === action.payload.id ? action.payload : v,
				),
			};
		case DELETE_VEHICLE_SUCCESS:
			return {
				...state,
				loading: false,
				vehicles: state.vehicles.filter((v) => v.id !== action.payload),
				total: state.total - 1,
			};
		case FETCH_VEHICLES_FAILURE:
		case CREATE_VEHICLE_FAILURE:
		case UPDATE_VEHICLE_FAILURE:
		case DELETE_VEHICLE_FAILURE:
			return { ...state, loading: false, error: action.payload };
		default:
			return state;
	}
}
